// Screen 6 — Propose → send (Stage 4). Confirm the tapped slot and send a
// standard-mode proposal. The recipient gets a magic-link email pointing back
// at when.vennio.app (responder_base_url), where they accept or decline. Accept
// writes the event into both calendars (server-side saga).
import { useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { sendProposal } from '../services/proposals';
import { Screen, Card, Eyebrow, Title, Button, ErrorState } from '../components/ui';

function formatSlot(start, end) {
  if (!start) return 'Selected slot';
  const s = new Date(start);
  const date = s.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  const from = s.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const to = end ? new Date(end).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : null;
  return to ? `${date}, ${from} – ${to}` : `${date}, ${from}`;
}

export default function Propose() {
  const { principalId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const start = params.get('start');
  const end = params.get('end');

  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState(null);

  const send = async () => {
    setStatus('sending'); setError(null);
    try {
      await sendProposal({ principalId, start, end });
      setStatus('sent');
    } catch (err) {
      setError('Couldn\'t send the proposal. Try again.');
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <Screen>
        <Card>
          <Eyebrow>proposal sent</Eyebrow>
          <Title className="mt-4 text-2xl">Sent.</Title>
          <p className="mt-4 font-sans text-base text-ink/70">
            We've emailed them a link to {formatSlot(start, end)}. When they accept,
            it lands in both your calendars.
          </p>
          <div className="mt-8">
            <Button onClick={() => navigate(`/with/${principalId}`)}>Back to overlap</Button>
          </div>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen>
      <button onClick={() => navigate(`/with/${principalId}`)} className="font-mono text-xs text-ink/40 hover:text-ink">← overlap</button>
      <Card className="mt-4">
        <Eyebrow>propose a time</Eyebrow>
        <Title className="mt-4 text-2xl">{formatSlot(start, end)}</Title>
        <p className="mt-4 font-sans text-base text-ink/70">
          We'll email them a link to accept or pick another time. Nothing lands in
          anyone's calendar until they accept.
        </p>

        {status === 'error' && error ? (
          <div className="mt-6"><ErrorState message={error} onRetry={send} /></div>
        ) : (
          <div className="mt-8">
            <Button onClick={send} disabled={status === 'sending' || !start}>
              {status === 'sending' ? 'Sending…' : 'Send proposal'}
            </Button>
          </div>
        )}
      </Card>
    </Screen>
  );
}
